import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ApiError } from '../api/client';

export function Loading({ label, fullPage = false }: { label: string; fullPage?: boolean }) {
  return (
    <div className={fullPage ? 'state state--loading state--page' : 'state state--loading'} role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  );
}

function describe(error: unknown): { title: string; body: string } {
  if (error instanceof ApiError) {
    if (error.isForbidden) {
      return {
        title: 'You do not have access to this',
        body: 'This material is restricted to other roles or named people. Access is checked on every request.',
      };
    }
    if (error.isNotFound) return { title: 'Not found', body: 'It may have been removed, or it is not visible to your account.' };
    if (error.status === 0) return { title: 'Connection problem', body: error.message };
    return { title: 'Something went wrong', body: error.message };
  }
  return { title: 'Something went wrong', body: 'An unexpected error occurred. Try again in a moment.' };
}

export function ErrorState({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  const described = describe(error);
  const code = error instanceof ApiError ? error.code : null;
  return (
    <div className="state state--error" role="alert">
      <p className="state__title">{title ?? described.title}</p>
      <p>{described.body}</p>
      {code !== null && <p className="state__code">Error code: {code}</p>}
      {onRetry !== undefined && (
        <button type="button" className="button button--secondary" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="state state--empty" role="status">
      <p className="state__title">{title}</p>
      {children}
    </div>
  );
}

/**
 * Renders a query's loading and error states consistently, handing the data to
 * `children` only once it has arrived.
 */
export function QueryState<T>({
  query,
  label,
  children,
}: {
  query: UseQueryResult<T>;
  label: string;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <Loading label={`Loading ${label}`} />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  return <>{children(query.data)}</>;
}
